import { Analysis, HintUnit, RuleInstance, TechniqueSig, sigKey } from '../rules';

// What makes one available deduction easier to *find* than another.
//
// The model is a competing-risks hazard: at any moment several deductions are available, each
// with its own rate of being spotted, and the one the player writes down next is whichever
// "fires" first. The rate is log-linear,
//
//     λ_i = exp( θ_p + α[sig_i] + δ_p[sig_i] + Σ_k β_k · x_k(i) )
//
// so time-to-next-deduction is Exp(Σλ) and the winner is i with probability λ_i/Σλ. Log-linear
// is not an aesthetic choice: it makes ∂λ/∂w = λ·x_w, which is what lets the fit run on analytic
// gradients in a hundred lines with no dependency.
//
// θ_p is how fast a player is in general, α[sig] is how hard a technique is for everyone, and
// δ_p[sig] is how much better or worse *this* player is at it. The features below carry
// everything that varies within a technique — how much board you have to scan, whether it is
// near what you were just looking at, whether you have used this idea recently.

export const FEATURE_NAMES = [
  'logScan',      // cells in the units reasoned about — the search area
  'logCands',     // live candidates in them — the part you actually have to think about
  'logYield',     // how much it accomplishes; big deductions are looked for harder
  'prox',         // closeness to the last cell touched: attention does not teleport
  'sameUnit',     // the deduction is about a unit you were just working in
  'unitRecency',  // …or one you were working in a few moves ago
  'sigRecency',   // you just used this technique, so you are primed to see it again
  'fill',         // how far through the puzzle you are — everything gets easier late
  'subsumed',     // a strictly better deduction is also available, so why take this one
  'logLive',      // how many deductions are on offer at all — see the note below
] as const;

// ── Why `logLive` exists ──
// Competing risks says the total rate is Σλ, so a board offering twice as many deductions gets
// solved twice as fast. Humans do not work that way: more options mean more to scan, not more
// throughput. Left uncorrected this showed up as a clean, measurable failure — the simulator
// under-predicted solve time by 0.6× on 7×7 boards and 0.22× on 11×11, exactly tracking how
// many deductions are simultaneously available. `logLive` is a per-observation constant that
// lets the fit discover how sub-linearly attention actually scales.

export const NF = FEATURE_NAMES.length;

/**
 * The board facts the features need, in a form both the fit (which has a full `Analysis` from
 * the rule engine) and the simulator (which keeps its own incremental counters, because building
 * an `Analysis` per simulated step would cost more than everything else combined) can supply.
 */
export interface BoardCounts {
  size: number;
  colorRegions: string[][];
  /** Live candidates in a row / column / region. */
  rowCands(i: number): number;
  colCands(i: number): number;
  regionCands(color: string): number;
  /** Cells belonging to a region, regardless of marks. */
  regionSize(color: string): number;
}

export function countsOfAnalysis(A: Analysis): BoardCounts {
  const regionSizes = new Map<string, number>();
  for (let r = 0; r < A.size; r++) {
    for (let c = 0; c < A.size; c++) {
      const col = A.colorRegions[r][c];
      regionSizes.set(col, (regionSizes.get(col) ?? 0) + 1);
    }
  }
  return {
    size: A.size,
    colorRegions: A.colorRegions,
    rowCands: i => (A.candByRow.get(i) ?? []).length,
    colCands: i => (A.candByCol.get(i) ?? []).length,
    regionCands: color => (A.candByColor.get(color) ?? []).length,
    regionSize: color => regionSizes.get(color) ?? 0,
  };
}

/** The shape both a `RuleInstance` and a graph `Support` satisfy. */
export interface Deduction {
  sig: TechniqueSig;
  units: HintUnit[];
  causeCells: string[];
  cells: string[];
}

/** Everything about "where the player's attention is" that the features need. */
export interface RateContext {
  A: BoardCounts;
  /** Marks visible on the board, for `fill`. */
  markedCount: number;
  /** The last cell the player marked, or null before their first move. */
  lastCell: [number, number] | null;
  /** unit key → index of the burst that last reasoned about it. */
  unitLastUsed: Map<string, number>;
  /** sigKey → index of the burst that last used it. */
  sigLastUsed: Map<string, number>;
  burstIndex: number;
  /** How many distinct deductions are available right now. Constant across a risk set. */
  liveCount: number;
}

export const unitKey = (u: HintUnit): string =>
  u.type === 'region' ? `region:${u.color}` : `${u.type}:${u.index}`;

/** Decay constant for the two recency features, in bursts. */
const RECENCY_TAU = 5;

function unitCellCount(u: HintUnit, A: BoardCounts): { cells: number; cands: number } {
  if (u.type === 'row') return { cells: A.size, cands: A.rowCands(u.index) };
  if (u.type === 'col') return { cells: A.size, cands: A.colCands(u.index) };
  return { cells: A.regionSize(u.color), cands: A.regionCands(u.color) };
}

function chebyshevToCause(causeCells: string[], from: [number, number]): number {
  let best = Infinity;
  for (const k of causeCells) {
    const [r, c] = k.split(',').map(Number);
    const d = Math.max(Math.abs(r - from[0]), Math.abs(c - from[1]));
    if (d < best) best = d;
  }
  return best;
}

/**
 * Feature row for one available deduction. `subsumedBy` is passed in because it is a property of
 * the whole risk set, not of the instance alone.
 */
export function featuresOf(inst: Deduction, ctx: RateContext, subsumed: boolean, out: Float32Array, at: number): void {
  let cells = 0, cands = 0;
  for (const u of inst.units) {
    const n = unitCellCount(u, ctx.A);
    cells += n.cells;
    cands += n.cands;
  }

  let prox = 0, sameUnit = 0;
  if (ctx.lastCell) {
    prox = Math.exp(-chebyshevToCause(inst.causeCells, ctx.lastCell) / 2);
    const [lr, lc] = ctx.lastCell;
    sameUnit = inst.units.some(u =>
      u.type === 'row' ? u.index === lr
        : u.type === 'col' ? u.index === lc
          : u.color === ctx.A.colorRegions[lr][lc]) ? 1 : 0;
  }

  let unitRecency = 0;
  for (const u of inst.units) {
    const last = ctx.unitLastUsed.get(unitKey(u));
    if (last !== undefined) {
      unitRecency = Math.max(unitRecency, Math.exp(-(ctx.burstIndex - last) / RECENCY_TAU));
    }
  }
  const lastSig = ctx.sigLastUsed.get(sigKey(inst.sig));
  const sigRecency = lastSig === undefined ? 0 : Math.exp(-(ctx.burstIndex - lastSig) / RECENCY_TAU);

  out[at + 0] = Math.log1p(cells);
  out[at + 1] = Math.log1p(cands);
  // Cells concluded, not the RuleInstance's `impact` — the simulator works from graph supports,
  // which have no impact field, and the two must compute identical features or a fitted
  // parameter means one thing in the fit and another in the simulation.
  out[at + 2] = Math.log1p(inst.cells.length);
  out[at + 3] = prox;
  out[at + 4] = sameUnit;
  out[at + 5] = unitRecency;
  out[at + 6] = sigRecency;
  out[at + 7] = ctx.markedCount / (ctx.A.size * ctx.A.size);
  out[at + 8] = subsumed ? 1 : 0;
  out[at + 9] = Math.log1p(ctx.liveCount);
}

/**
 * Which instances are strictly dominated: another available deduction concludes everything this
 * one does and more. Quadratic in the risk-set size, but risk sets are tens of instances and the
 * cell lists are short, so it is cheap next to the enumeration that produced them.
 */
export function markSubsumed(risk: Array<{ kind?: string; cells: string[] }>): boolean[] {
  const sets = risk.map(r => new Set(r.cells));
  return risk.map((r, i) => {
    for (let j = 0; j < risk.length; j++) {
      if (j === i || risk[j].kind !== r.kind || sets[j].size <= sets[i].size) continue;
      let covers = true;
      for (const c of r.cells) if (!sets[j].has(c)) { covers = false; break; }
      if (covers) return true;
    }
    return false;
  });
}

// ---------------------------------------------------------------------------
// Parameters
// ---------------------------------------------------------------------------

/**
 * Technique signatures, densely numbered. Built once from a corpus so the α and δ vectors have
 * a stable layout that `reference.json` can be written against.
 */
export class SigVocab {
  private index = new Map<string, number>();
  readonly keys: string[] = [];

  static from(keys: Iterable<string>): SigVocab {
    const v = new SigVocab();
    for (const k of [...new Set(keys)].sort()) v.add(k);
    return v;
  }

  add(key: string): number {
    let i = this.index.get(key);
    if (i === undefined) { i = this.keys.length; this.index.set(key, i); this.keys.push(key); }
    return i;
  }

  /** -1 for a signature the fit never saw; callers treat that as α = 0. */
  idOf(key: string): number {
    return this.index.get(key) ?? -1;
  }

  get size(): number { return this.keys.length; }
}

export interface MotorParams {
  /** Fixed cost of starting a burst of marking, ms. */
  interceptMs: number;
  /** Marginal cost per cell marked, ms. */
  perCellMs: number;
}

export interface PlayerParams {
  /** Overall speed. Higher ⇒ everything is spotted sooner. */
  theta: number;
  /** Per-signature proficiency offsets, shrunk toward 0. Length = vocab.size. */
  delta: Float64Array;
  motor: MotorParams;
}

export interface ModelParams {
  sigKeys: string[];
  /** Global per-signature difficulty. Length = sigKeys.length. */
  alpha: Float64Array;
  /** Feature weights. Length = NF. */
  beta: Float64Array;
  players: Record<string, PlayerParams>;
}

/** log λ for one deduction. `x` is a feature row starting at `at`. */
export function logRate(
  alpha: Float64Array, beta: Float64Array, theta: number, delta: Float64Array | null,
  sigId: number, x: Float32Array, at: number,
): number {
  let s = theta;
  if (sigId >= 0) {
    s += alpha[sigId];
    if (delta) s += delta[sigId];
  }
  for (let k = 0; k < NF; k++) s += beta[k] * x[at + k];
  return s;
}

// ---------------------------------------------------------------------------
// Observations
// ---------------------------------------------------------------------------

/**
 * One timed decision, in the form the likelihood consumes.
 *
 * `x` holds the feature rows for every deduction that was *available* when the player started
 * looking (the risk set) — the survival term needs all of them, because the observation is not
 * just "they found this" but "they found this and not any of those, and it took `gapMs`".
 * `candidates` indexes the rows that could explain what they actually did; the model resolves
 * that ambiguity itself as a posterior rather than us picking a winner up front.
 */
export interface BurstObservation {
  gapMs: number;
  nRisk: number;
  /** nRisk × NF, row-major. */
  x: Float32Array;
  /** Signature key per risk row. */
  sigKeys: string[];
  /** Indices into the risk rows that explain the move made. Never empty. */
  candidates: number[];
}

/** Distinct *opportunities*: two instances concluding the same cells by the same technique are
 *  one thing to look for, not two. Without this, R9's one-instance-per-unit-pair emission puts
 *  hundreds of restatements into every risk set and swamps the survival term. */
export function dedupeRisk(risk: RuleInstance[]): RuleInstance[] {
  const seen = new Set<string>();
  const out: RuleInstance[] = [];
  for (const r of risk) {
    const k = `${sigKey(r.sig)}|${r.kind}|${r.cells.join(' ')}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}
